<!-- src/apps/secret/components/conceal/landing/SocialProof.vue -->

<script setup lang="ts">
  /**
   * Landing social-proof band (anonymous visitors only).
   *
   * Two honest trust signals:
   *
   *  1) A "secrets protected" counter, sourced from the SAME public aggregate
   *     endpoint the /status page uses (GET /api/v2/status/metrics, auth=noauth).
   *     We read `metrics.secrets_created_total` — a safe lifetime aggregate, no
   *     PII, no secret content. The fetch mirrors StatusView's honest pattern:
   *     loading → loaded/error, and on any failure we simply HIDE the number
   *     (never invent one).
   *
   *  2) Trust seals — LOCAL/trust signals (🇧🇷 Data in Brazil · PT support · No
   *     signup), always rendered. Technical claims (AES-256, LGPD detail) live in
   *     the specs sheet now, so the band is not redundant with it.
   *
   * Anti-weak-number gate: a freshly launched product may have a tiny total.
   * "27 secrets protected" reads amateur, so the counter only appears once the
   * total clears COUNTER_MIN_THRESHOLD; below that we show the seals alone.
   */
  import { systemStatusMetricsResponseSchema } from '@/schemas/api/v2/responses/meta';
  import { computed, onMounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  const { t, locale } = useI18n();

  // Show the counter with prominence only once it is a number worth showing.
  // Below this, the counter is hidden entirely (seals still render).
  const COUNTER_MIN_THRESHOLD = 500;

  type LoadState = 'idle' | 'loading' | 'loaded' | 'error';

  const state = ref<LoadState>('idle');
  const secretsTotal = ref<number | null>(null);

  async function load() {
    if (state.value === 'loading') return;
    state.value = 'loading';
    try {
      const res = await fetch('/api/v2/status/metrics', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const parsed = systemStatusMetricsResponseSchema.safeParse(json);
      if (!parsed.success) {
        console.warn('[social-proof] response failed validation', parsed.error);
        throw parsed.error;
      }
      secretsTotal.value = parsed.data.metrics.secrets_created_total;
      state.value = 'loaded';
    } catch (err) {
      // Honest failure: hide the number, keep the seals.
      console.warn('[social-proof] Failed to load status metrics', err);
      state.value = 'error';
    }
  }

  // The counter is shown only on a successful load AND once the real total
  // clears the threshold — never on loading/error, never below the floor.
  const showCounter = computed(
    () =>
      state.value === 'loaded' &&
      typeof secretsTotal.value === 'number' &&
      secretsTotal.value >= COUNTER_MIN_THRESHOLD
  );

  // Thousand-separated for the active UI locale (pt-BR → "1.234").
  const formattedTotal = computed(() => {
    if (typeof secretsTotal.value !== 'number') return '';
    const intlLocale = String(locale.value).startsWith('pt') ? 'pt-BR' : 'en-US';
    return secretsTotal.value.toLocaleString(intlLocale);
  });

  // Trust band = LOCAL / trust signals, deliberately NOT repeating the technical
  // specs (AES-256, LGPD detail live in the specs sheet now). data_br carries a
  // Brazilian flag to signal the secrets are hosted on a Brazilian server.
  const seals = [
    { key: 'data_br', labelKey: 'landing.social.seal_data_br', flag: true },
    { key: 'pt_support', labelKey: 'landing.social.seal_pt_support', flag: false },
    { key: 'no_signup', labelKey: 'landing.social.seal_no_signup', flag: false },
  ];

  onMounted(load);
</script>

<template>
  <section
    class="border-b border-day-line dark:border-white/[0.07]"
    data-testid="social-proof"
    aria-label="Trust">
    <div
      class="flex flex-col gap-8 p-6 py-11 sm:p-9 lg:flex-row lg:items-center lg:gap-12 lg:py-12">
      <!-- Counter (gated: only >= threshold, only on successful load) -->
      <div
        v-if="showCounter"
        class="flex shrink-0 items-baseline gap-3.5 lg:border-r lg:border-day-line lg:pr-12 dark:lg:border-white/[0.07]"
        data-testid="social-proof-count">
        <span
          class="font-mono text-4xl font-bold tracking-[-0.035em] text-day-ink lg:text-5xl dark:text-night-ink"
          data-testid="social-proof-total">
          {{ formattedTotal }}
        </span>
        <span
          class="max-w-[7rem] font-mono text-[10px] leading-[1.3] tracking-[0.1em] text-day-ink-muted uppercase dark:text-night-ink-muted">
          {{ t('landing.social.count_label') }}
        </span>
      </div>

      <!-- Trust seals (always visible) -->
      <ul
        class="flex flex-wrap items-center gap-3"
        data-testid="social-proof-seals">
        <li
          v-for="seal in seals"
          :key="seal.key"
          class="inline-flex items-center gap-2.5 border px-3.5 py-2"
          :class="
            seal.flag
              ? 'border-brand-500/50 dark:border-brand-400/40'
              : 'border-day-line dark:border-white/[0.09]'
          ">
          <span
            v-if="seal.flag"
            class="text-sm leading-none"
            role="img"
            aria-label="Brasil">🇧🇷</span>
          <span
            v-else
            class="inline-block size-1.5 shrink-0 rounded-full bg-brand-500 shadow-[0_0_8px_var(--color-brand-500)]"
            aria-hidden="true"></span>
          <span
            class="font-mono text-[10px] tracking-[0.1em] text-day-ink-muted uppercase dark:text-night-ink-muted">
            {{ t(seal.labelKey) }}
          </span>
        </li>
      </ul>
    </div>
  </section>
</template>
